/**
 * /reranker — internal book affinity reranker tool.
 *
 * Default mode scores the whole catalog for one Shopify customer: the top-K
 * nearest books from the Qdrant book_cf collection are retrieved and scored.
 * The secondary mode (under "More options") scores a pasted list of book ids
 * instead. Results render as a cover grid; the Details switch swaps in the
 * customer summary and the feature table. While the API scores, the shared
 * LoadingScreen cycles through book jokes.
 */
import { ChevronDown, RefreshCw, Sparkles } from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAffinity } from '../hooks/useAffinity';
import { formatNumber } from '../i18n/format';
import type { AffinityDocument } from '../services/api';
import AffinityFeedbackModal from './AffinityFeedbackModal';
import AffinityGrid from './AffinityGrid';
import AffinityTable from './AffinityTable';
import FeedbackSideTab, { type FeedbackChoice } from './FeedbackSideTab';
import IndigoLogo from './IndigoLogo';
import LoadingScreen from './LoadingScreen';

const MAX_BOOK_IDS = 1000;
const TOP_K_PRESETS = [100, 250, 500, 1000];
const DEFAULT_TOP_K = 1000;

type InputMode = 'all' | 'ids';

function parseBookIds(raw: string): string[] {
  const ids = raw
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return Array.from(new Set(ids));
}

function CustomerSummary({ document }: Readonly<{ document: AffinityDocument }>) {
  const { t } = useTranslation();
  const customer = document.customer;
  if (!customer) return null;
  const mix = Object.entries(customer.audience_mix || {})
    .sort((a, b) => b[1] - a[1])
    .map(
      ([k, v]) =>
        `${t(`affinity.audience.${k}`, { defaultValue: k })} ${formatNumber(Math.round(v * 100))} %`
    )
    .join(' · ');
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 space-y-1">
      <p>
        <span className="font-medium">{t('affinity.summary.scorer')}</span>{' '}
        <span className="font-mono text-xs">{document.scorer}</span>
        {document.cf_version ? (
          <span className="font-mono text-xs text-gray-500"> · {document.cf_version}</span>
        ) : null}
        {typeof document.execution_ms === 'number' ? (
          <span className="text-gray-500">
            {' '}
            · {formatNumber(Math.round(document.execution_ms))} ms
          </span>
        ) : null}
      </p>
      <p>
        <span className="font-medium">{t('affinity.summary.vectors')}</span>{' '}
        {customer.has_cf_user_vector ? 'CF' : '—'} / {customer.has_desc_vector ? 'DESC' : '—'}
        {' · '}
        {t('affinity.summary.ownedCount', { count: customer.owned_count })}
      </p>
      {mix ? (
        <p>
          <span className="font-medium">{t('affinity.summary.audience')}</span> {mix}
        </p>
      ) : null}
      {customer.top_level_genres.length > 0 ? (
        <p>
          <span className="font-medium">{t('affinity.summary.genres')}</span>{' '}
          {customer.top_level_genres.join(', ')}
        </p>
      ) : null}
    </div>
  );
}

function ModeToggle({
  mode,
  onChange,
}: Readonly<{ mode: InputMode; onChange: (mode: InputMode) => void }>) {
  const { t } = useTranslation();
  const options: InputMode[] = ['all', 'ids'];
  return (
    <fieldset className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5 text-sm">
      <legend className="sr-only">{t('affinity.form.mode')}</legend>
      {options.map((option) => (
        <label
          key={option}
          className={`cursor-pointer rounded-md px-3 py-1.5 font-medium transition ${
            mode === option ? 'bg-indigo-brand-text text-white' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <input
            type="radio"
            name="affinity-mode"
            value={option}
            checked={mode === option}
            onChange={() => onChange(option)}
            className="sr-only"
          />
          {t(`affinity.form.mode_${option}`)}
        </label>
      ))}
    </fieldset>
  );
}

function DetailsSwitch({
  on,
  onChange,
}: Readonly<{ on: boolean; onChange: (on: boolean) => void }>) {
  const { t } = useTranslation();
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700">
      <input
        type="checkbox"
        role="switch"
        aria-checked={on}
        checked={on}
        onChange={(e) => onChange(e.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className="relative h-5 w-9 rounded-full bg-gray-300 transition peer-checked:bg-indigo-brand-text peer-focus-visible:ring-2 peer-focus-visible:ring-indigo-brand-text/40 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:shadow after:transition peer-checked:after:translate-x-4"
      />
      {t('affinity.view.details')}
    </label>
  );
}

export default function AffinityPage() {
  const { t } = useTranslation();
  const [customerId, setCustomerId] = useState('');
  const [inputMode, setInputMode] = useState<InputMode>('all');
  const [showDetails, setShowDetails] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [bookIdsRaw, setBookIdsRaw] = useState('');
  const [topK, setTopK] = useState(DEFAULT_TOP_K);
  const [lastForce, setLastForce] = useState(false);
  const [lastRunCustomer, setLastRunCustomer] = useState('');
  const [feedbackChoice, setFeedbackChoice] = useState<FeedbackChoice | null>(null);
  const { phase, document, error, submit } = useAffinity();

  const jokes = useMemo(() => t('affinity.jokes', { returnObjects: true }) as string[], [t]);
  const bookIds = useMemo(() => parseBookIds(bookIdsRaw), [bookIdsRaw]);
  const useIds = inputMode === 'ids';
  const tooMany = useIds && bookIds.length > MAX_BOOK_IDS;
  const idsMissing = useIds && bookIds.length === 0;
  const canRun = customerId.trim().length > 0 && !tooMany && !idsMissing;

  const run = (force: boolean) => {
    if (!canRun) return;
    setLastForce(force);
    setLastRunCustomer(customerId.trim());
    void submit({
      shopify_customer_id: customerId.trim(),
      mode: useIds ? 'book_ids' : 'retrieve',
      book_ids: useIds ? bookIds : undefined,
      top_k: useIds ? undefined : topK,
      force,
    });
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    run(false);
  };

  if (phase === 'loading') {
    return (
      <LoadingScreen
        messages={jokes.length > 0 ? jokes : [t('common.loading')]}
        intervalMs={3200}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white font-body">
      <header className="border-b border-indigo-brand-border px-6 py-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <IndigoLogo className="h-7 w-auto self-start sm:h-9 sm:self-auto" />
          <div className="sm:border-l sm:border-indigo-brand-border sm:pl-4">
            <h1 className="text-xl font-semibold text-gray-900">{t('affinity.title')}</h1>
            <p className="text-sm text-gray-500">{t('affinity.subtitle')}</p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-6 space-y-6 sm:pr-14">
        <form onSubmit={onSubmit} className="mx-auto max-w-2xl space-y-3">
          <label htmlFor="affinity-customer" className="sr-only">
            {t('affinity.form.customerId')}
          </label>
          <div className="flex gap-2 rounded-2xl border border-gray-300 bg-white p-1.5 shadow-sm focus-within:border-indigo-brand-text focus-within:ring-2 focus-within:ring-indigo-brand-text/20">
            <input
              id="affinity-customer"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              placeholder={t('affinity.form.customerIdPlaceholder')}
              className="min-w-0 flex-1 rounded-xl px-3 py-2.5 text-base focus:outline-none"
              inputMode="numeric"
              // biome-ignore lint/a11y/noAutofocus: single-purpose internal tool; the ID is the only required input
              autoFocus
            />
            <button
              type="submit"
              disabled={!canRun}
              className="inline-flex items-center gap-2 rounded-xl bg-indigo-brand-text px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              <Sparkles size={16} /> {t('affinity.form.submit')}
            </button>
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>
              {useIds
                ? t('affinity.form.bookIdCount', { count: bookIds.length })
                : t('affinity.form.allBooksSummary', { total: formatNumber(topK) })}
            </span>
            <button
              type="button"
              onClick={() => setShowOptions((v) => !v)}
              aria-expanded={showOptions}
              className="inline-flex items-center gap-1 font-medium text-gray-600 hover:text-gray-900"
            >
              {t('affinity.form.options')}
              <ChevronDown size={14} className={`transition ${showOptions ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {showOptions ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <span className="block text-sm font-medium text-gray-700">
                    {t('affinity.form.mode')}
                  </span>
                  <div className="mt-1">
                    <ModeToggle mode={inputMode} onChange={setInputMode} />
                  </div>
                </div>
                {useIds ? null : (
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">
                      {t('affinity.form.topK')}
                    </span>
                    <select
                      value={topK}
                      onChange={(e) => setTopK(Number(e.target.value))}
                      className="mt-1 block rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    >
                      {TOP_K_PRESETS.map((k) => (
                        <option key={k} value={k}>
                          {formatNumber(k)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => run(true)}
                  disabled={!canRun}
                  title={t('affinity.status.cachedHint')}
                  className="ml-auto inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 disabled:opacity-50"
                >
                  <RefreshCw size={14} /> {t('affinity.form.recompute')}
                </button>
              </div>
              {useIds ? null : (
                <p className="text-xs text-gray-500">{t('affinity.form.allBooksHint')}</p>
              )}
            </div>
          ) : null}

          {useIds && showOptions ? (
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t('affinity.form.bookIds')}
              </span>
              <textarea
                value={bookIdsRaw}
                onChange={(e) => setBookIdsRaw(e.target.value)}
                rows={4}
                placeholder={t('affinity.form.bookIdsPlaceholder')}
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-mono focus:border-indigo-brand-text focus:outline-none"
              />
              <p className={`mt-1 text-xs ${tooMany ? 'text-red-600' : 'text-gray-500'}`}>
                {tooMany
                  ? t('affinity.form.tooMany', { max: MAX_BOOK_IDS })
                  : t('affinity.form.bookIdCount', { count: bookIds.length })}
              </p>
            </label>
          ) : null}
        </form>

        {phase === 'error' ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {t('affinity.status.error')} {error}
          </div>
        ) : null}

        {phase === 'ready' && document ? (
          <section className="space-y-4">
            {document.status === 'no_customer_vectors' ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {t('affinity.status.noVectors')}
              </div>
            ) : null}
            {document.status === 'error' ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {t('affinity.status.error')} {document.error}
              </div>
            ) : null}
            {document.status === 'ready' ? (
              <>
                {showDetails ? <CustomerSummary document={document} /> : null}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-gray-600">
                    {t('affinity.results.count', { count: (document.books || []).length })}
                    {document.mode === 'retrieve' ? ` · ${t('affinity.results.fromCatalog')}` : ''}
                    {lastForce ? '' : ` · ${t('affinity.status.cachedHint')}`}
                  </p>
                  <DetailsSwitch on={showDetails} onChange={setShowDetails} />
                </div>
                {document.unresolved_book_ids && document.unresolved_book_ids.length > 0 ? (
                  <p className="text-xs text-gray-500">
                    {t('affinity.results.unresolved', {
                      count: document.unresolved_book_ids.length,
                    })}{' '}
                    <span className="font-mono">{document.unresolved_book_ids.join(', ')}</span>
                  </p>
                ) : null}
                {showDetails ? (
                  <AffinityTable rows={document.books || []} scoreType={document.score_type} />
                ) : (
                  <AffinityGrid rows={document.books || []} scoreType={document.score_type} />
                )}
              </>
            ) : null}
          </section>
        ) : null}
      </main>

      {lastRunCustomer ? (
        <FeedbackSideTab
          onSelect={setFeedbackChoice}
          bodyKeys={{ rate: 'affinity.feedback.panelRateBody' }}
        />
      ) : null}
      {feedbackChoice && lastRunCustomer ? (
        <AffinityFeedbackModal
          mode={feedbackChoice}
          shopifyCustomerId={lastRunCustomer}
          document={phase === 'ready' ? document : null}
          topK={topK}
          onClose={() => setFeedbackChoice(null)}
        />
      ) : null}
    </div>
  );
}
